import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SiteNav from "@/components/layout/SiteNav";
import SiteFooter from "@/components/layout/SiteFooter";
import { ArrowRight, Sparkles, FileText, Search, Stamp, Scale, FolderLock, Bot, Clock, Share2, Crown, Loader2 } from "lucide-react";

const SECTION_ICONS = {
    search: Search,
    apply: FileText,
    affidavits: Stamp,
    tax_fight: Scale,
    vault: FolderLock,
    ai: Bot,
    tracking: Clock,
    share_privacy: Share2,
    creator: Crown,
};

/**
 * Public capabilities page. Data is fetched from /api/capabilities (manifest
 * lives at backend/data/capabilities.json) — so every time a new feature ships,
 * appending to that JSON is the ONLY change needed to make it show up here.
 */
export default function Capabilities() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

    useEffect(() => {
        let cancel = false;
        axios.get(`${backend}/api/capabilities`)
            .then(r => { if (!cancel) setData(r.data); })
            .catch(e => { if (!cancel) setErr(e?.response?.data?.detail || "Couldn't load capabilities"); });
        return () => { cancel = true; };
    }, [backend]);

    if (err) return (
        <div className="min-h-screen bg-background text-foreground">
            <SiteNav />
            <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center">
                <p className="text-sm text-destructive">{err}</p>
            </div>
            <SiteFooter />
        </div>
    );

    if (!data) return (
        <div className="min-h-screen bg-background text-foreground">
            <SiteNav />
            <div className="max-w-3xl mx-auto px-5 sm:px-8 py-32 text-center">
                <Loader2 className="h-6 w-6 text-muted-foreground mx-auto animate-spin" />
            </div>
            <SiteFooter />
        </div>
    );

    const totalFeatures = (data.categories || []).reduce((n, c) => n + (c.features?.length || 0), 0);

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="capabilities-page">
            <SiteNav />
            <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14">
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary border border-border px-3 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5" /> Owed · capabilities · v{data.version}
                </div>
                <h1 className="mt-6 font-serif text-5xl sm:text-6xl leading-tight tracking-tight max-w-3xl">{data.app?.tagline}</h1>
                <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">{data.app?.summary}</p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Button asChild size="lg" className="rounded-full h-12 px-7 bg-foreground text-background hover:bg-foreground/90" data-testid="capabilities-cta-primary">
                        <Link to="/onboarding">Run my free scan <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7">
                        <Link to="/demo">See a sample</Link>
                    </Button>
                    <Badge variant="outline" className="bg-secondary border-border text-xs h-9 px-4 inline-flex items-center">
                        {data.app?.price}
                    </Badge>
                </div>

                <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Stat label="Capability categories" value={data.categories?.length || 0} />
                    <Stat label="Distinct features" value={totalFeatures} />
                    <Stat label="Affidavit templates" value={data.categories?.find(c => c.id === "affidavits")?.features?.length || 0} />
                    <Stat label="Last updated" value={data.updated || "—"} small />
                </div>

                <div className="mt-14 space-y-8">
                    {(data.categories || []).map(cat => {
                        const Icon = SECTION_ICONS[cat.id] || Sparkles;
                        return (
                            <section key={cat.id} className="border-t border-border pt-8" data-testid={`cap-section-${cat.id}`}>
                                <div className="flex items-start gap-3">
                                    <span className="h-11 w-11 rounded-xl bg-foreground text-background inline-flex items-center justify-center shrink-0">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <h2 className="font-serif text-3xl leading-tight">{cat.title}</h2>
                                        <p className="mt-1.5 text-muted-foreground max-w-2xl leading-relaxed">{cat.blurb}</p>
                                    </div>
                                    <Badge variant="outline" className="bg-secondary/60 border-border text-[10px] font-mono shrink-0">{cat.features?.length || 0}</Badge>
                                </div>
                                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {(cat.features || []).map((f, i) => (
                                        <Card key={f.name || `${cat.id}-${i}`} className="p-5 border-border hover:border-foreground/40 transition-colors" data-testid={`cap-feature-${cat.id}-${i}`}>
                                            <p className="font-serif text-base leading-tight">{f.name}</p>
                                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.details}</p>
                                            {f.where && (
                                                <p className="mt-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                                    {f.where}
                                                </p>
                                            )}
                                        </Card>
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </div>

                {data.app?.creator_note && (
                    <div className="mt-14 rounded-2xl border-2 border-accent/40 bg-gradient-to-br from-accent/10 via-card to-card p-5 inline-flex items-start gap-3">
                        <Crown className="h-5 w-5 text-accent-foreground shrink-0 mt-0.5" />
                        <p className="text-sm text-foreground leading-relaxed">{data.app.creator_note}</p>
                    </div>
                )}

                <div className="mt-14 border-t border-border pt-10 text-center">
                    <h3 className="font-serif text-3xl">Ready to claim it?</h3>
                    <p className="mt-2 text-muted-foreground">{data.app?.price}. No subscription. Built for you.</p>
                    <Button asChild size="lg" className="mt-6 rounded-full h-12 px-7 bg-foreground text-background hover:bg-foreground/90">
                        <Link to="/onboarding">Start now <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                </div>
            </div>
            <SiteFooter />
        </div>
    );
}

function Stat({ label, value, small }) {
    return (
        <Card className="p-4 border-border">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={`mt-1.5 font-serif ${small ? "text-base" : "text-3xl"}`}>{value}</p>
        </Card>
    );
}
